// src/features/dashboard/components/CustomerStatusSection.tsx

import React from 'react';

import {
  UserPlus,
  Users,
  UserRoundCheck,
  UserRoundX,
} from 'lucide-react';

import type {
  CustomerStatus,
} from '../types/adminDashboard.types';


type CustomerStatusSectionProps = {
  data: CustomerStatus;
};


type CustomerStatusItemProps = {
  title: string;

  description: string;

  value: number;

  icon: React.ReactNode;

  valueClassName?: string;
};


const CustomerStatusItem:
  React.FC<
    CustomerStatusItemProps
  > = ({
    title,
    description,
    value,
    icon,
    valueClassName = 'text-gray-900',
  }) => {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3.5">

        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white text-gray-500 shadow-sm">
          {icon}
        </span>


        <div className="min-w-0 flex-1">

          <p className="text-sm font-semibold text-gray-800">
            {title}
          </p>


          <p className="mt-0.5 truncate text-[11px] text-gray-400">
            {description}
          </p>

        </div>


        <div className="shrink-0 text-right">

          <strong
            className={`
              text-lg font-black
              ${valueClassName}
            `}
          >
            {value.toLocaleString()}
          </strong>


          <span className="ml-1 text-xs text-gray-400">
            명
          </span>

        </div>

      </div>
    );
  };


const CustomerStatusSection:
  React.FC<
    CustomerStatusSectionProps
  > = ({
    data,
  }) => {
    const {
      recent7Days,
      active30Days,
      newUsers,
      inactive14Days,
    } = data;


    return (
      <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">

        {/* Header */}
        <div className="border-b border-gray-100 px-4 py-4 sm:px-5">

          <div className="flex items-center gap-3">

            <span className="grid h-10 w-10 place-items-center rounded-xl bg-main/10 text-main">
              <Users
                size={20}
              />
            </span>


            <div>

              <h2 className="text-base font-bold text-gray-900">
                고객 현황
              </h2>


              <p className="mt-0.5 text-xs text-gray-400">
                최근 고객 활동 상태를 확인합니다.
              </p>

            </div>

          </div>

        </div>


        {/* Body */}
        <div className="space-y-2.5 p-4 sm:p-5">

          <CustomerStatusItem
            title="최근 7일 방문"
            description="최근 일주일 내 매장을 방문한 고객"
            value={
              recent7Days
            }
            icon={
              <UserRoundCheck
                size={19}
              />
            }
          />


          <CustomerStatusItem
            title="최근 30일 활성회원"
            description="최근 한 달 내 방문 기록이 있는 고객"
            value={
              active30Days
            }
            icon={
              <Users
                size={19}
              />
            }
          />


          <CustomerStatusItem
            title="신규 고객"
            description="최근 등록된 신규 고객"
            value={
              newUsers
            }
            icon={
              <UserPlus
                size={19}
              />
            }
            valueClassName="text-main"
          />


          <CustomerStatusItem
            title="14일 이상 미방문"
            description="재방문 관리가 필요한 고객"
            value={
              inactive14Days
            }
            icon={
              <UserRoundX
                size={19}
              />
            }
            valueClassName={
              inactive14Days >
              0
                ? 'text-orange-600'
                : 'text-gray-900'
            }
          />

        </div>


        {/* 관리 힌트 */}
        {inactive14Days >
          0 && (

          <div className="border-t border-orange-100 bg-orange-50 px-4 py-3 sm:px-5">

            <p className="text-xs leading-5 text-orange-700">
              최근 14일 이상 방문하지 않은 고객이{' '}
              <strong>
                {inactive14Days.toLocaleString()}
                명
              </strong>{' '}
              있습니다. 재방문 관리가 필요한 고객군입니다.
            </p>

          </div>

        )}

      </section>
    );
  };


export default CustomerStatusSection;